// marketingTagRuntime.ts — CORE-1142
//
// Платформенная половина внешнего маркетингового слоя: подключение счётчиков и
// отправка в них целей. Правила («что во что отображается», «кому что
// положено») — в `marketingTags.ts`; здесь только исполнение.
//
// Веб-only по построению. Нативные приложения в первый платный тест не входят
// (протокол 11.08 — VK и веб-посадочная), а тащить мобильные SDK площадок ради
// слоя, который ещё не согласован с Legal, значило бы протащить в бандл
// стороннюю телеметрию раньше решения о ней.

import { Platform } from 'react-native';
import { analytics } from './analytics';
import { readState, webEntrySnapshot } from './acquisition';
import { storage } from '../utils/storage';
import { sameCookieChoice } from './cookieChoice';
import {
  applyGoal,
  environmentAllows,
  goalsForEvent,
  goalParams,
  metrikaLandingHit,
  sanitizeGoalParams,
  siteVisitGoal,
  parseEnvironments,
  parseTagConfig,
  providersForRegion,
  resolveTagRegion,
  shouldLoadTags,
  MARKETING_DEDUP_KEY,
  METRIKA_SCRIPT_TIMEOUT_MS,
  METRIKA_STATUS_EVENT,
  METRIKA_STATUS_SETTLE_MS,
  MetrikaStatusTracker,
  type DedupLedger,
  type MetrikaStatusProps,
  type GoalHit,
  type MarketingConsent,
  type MarketingGoal,
  type TagProvider,
  type TagRegion,
} from './marketingTags';

/** Схема целиком приходит конфигом — см. шапку `marketingTags.ts`. */
const TAG_CONFIG = process.env.EXPO_PUBLIC_MARKETING_TAGS;
const TAG_ENVIRONMENTS = process.env.EXPO_PUBLIC_MARKETING_TAGS_ENVIRONMENTS;

type Win = Record<string, any>;

function win(): Win | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  return window as unknown as Win;
}

function injectScript(src: string, onDone?: (ok: boolean) => void): void {
  const w = win();
  if (!w || typeof document === 'undefined') return;
  if (document.querySelector(`script[src="${src}"]`)) return;
  const el = document.createElement('script');
  el.async = true;
  el.src = src;
  if (onDone) {
    // CORE-1853 — блокировщик и обрыв сети видны ровно здесь: `error` у тега.
    el.addEventListener('load', () => onDone(true));
    el.addEventListener('error', () => onDone(false));
  }
  document.head.appendChild(el);
}

// ── Загрузчики площадок ─────────────────────────────────────────────────────

/**
 * Яндекс.Метрика.
 *
 * `webvisor: false` — не декоративная настройка. Вебвизор записывает сеанс
 * целиком, включая содержимое полей, то есть ровно те тексты и адреса, которых
 * в маркетинговой аналитике быть не должно. Включать его можно только отдельным
 * решением с Legal, и оно здесь не подразумевается.
 *
 * `clickmap: false` по той же причине мельче: карта кликов сама по себе безобидна,
 * но это ещё один поток поведенческих данных, которого критерий «нет PII» не
 * требует.
 */
function loadMetrika(w: Win, id: string, onScript?: (ok: boolean) => void): void {
  if (!w.ym) {
    w.ym = function tagQueue(...args: unknown[]) {
      (w.ym.a = w.ym.a || []).push(args);
    };
    w.ym.l = Date.now();
    injectScript('https://mc.yandex.ru/metrika/tag.js', onScript);
  }
  w.ym(id, 'init', {
    clickmap: false,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: false,
    defer: true,
  });
}

/**
 * Google Analytics 4.
 *
 * `allow_google_signals: false` и `allow_ad_personalization_signals: false`
 * отключают связывание визита с рекламным профилем Google. Отчёту по кампаниям
 * они не нужны, а согласия ровно на это у нас нет.
 */
function loadGa4(w: Win, id: string): void {
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    w.gtag = function gtag(...args: unknown[]) {
      w.dataLayer.push(args);
    };
    w.gtag('js', new Date());
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`);
  }
  w.gtag('config', id, {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    send_page_view: false,
  });
}

/** Пиксель VK (top.mail.ru) — первый платный канал по протоколу 11.08. */
function loadVk(w: Win, id: string): void {
  w._tmr = w._tmr || [];
  w._tmr.push({ id, type: 'pageView', start: Date.now() });
  injectScript('https://top-fwz1.mail.ru/js/code.js');
}

// ── Рантайм ─────────────────────────────────────────────────────────────────

class MarketingTagRuntime {
  private providers: TagProvider[] = parseTagConfig(TAG_CONFIG);
  private environments: string[] = parseEnvironments(TAG_ENVIRONMENTS);
  private consent: MarketingConsent = 'unknown';
  private region: TagRegion = 'unknown';
  private active: TagProvider[] = [];
  /** Виды, чей скрипт уже на странице: повторно не подключаем. */
  private loaded = new Set<TagProvider['kind']>();
  private unsubscribe: (() => void) | null = null;
  private ledger: DedupLedger | null = null;
  private ledgerRead = false;
  private lastPath: string | null = null;
  private metrikaLandingPath: string | null = null;
  private metrikaStatus = new MetrikaStatusTracker();
  private metrikaStatusArmed = false;

  /** Есть ли вообще что подключать. Публично — для отладочных поверхностей. */
  isConfigured(): boolean {
    return this.providers.length > 0;
  }

  /**
   * Может ли слой включиться в этом окружении при согласии.
   *
   * Отдельно от `isConfigured`, потому что этим ответом решается, показывать ли
   * запрос согласия: спрашивать разрешение на счётчики, которых на стенде всё
   * равно не будет, — это обучать людей закрывать баннер не читая, а потом
   * получить формально данное согласие, которого никто не давал осознанно.
   */
  isEligible(): boolean {
    if (!this.providers.length) return false;
    return environmentAllows(this.host(), this.environments);
  }

  /**
   * CORE-1659 — счётчики, которые у этого посетителя вообще могут включиться:
   * настроены, окружение подходит, регион — его (пока регион не известен, все
   * настроенные). По этому списку окно настроек cookie называет, что стоит за
   * каждой категорией, — а не по зашитому в текст перечню.
   */
  availableProviders(): TagProvider[] {
    if (!this.isEligible()) return [];
    return this.region === 'unknown' ? this.providers : providersForRegion(this.providers, this.region);
  }

  /** Сообщить выбор по категориям. Отзыв останавливает отправку. */
  setConsent(consent: MarketingConsent): void {
    if (this.consent === consent) return;
    if (typeof this.consent === 'object' && typeof consent === 'object' && sameCookieChoice(this.consent, consent)) return;
    this.consent = consent;
    this.evaluate();
  }

  /** Сообщить регион (код страны из GeoIP; `null` — ещё не известен). */
  setCountry(country: string | null | undefined): void {
    const region = resolveTagRegion(country);
    if (this.region === region) return;
    this.region = region;
    this.evaluate();
  }

  private host(): string | null {
    const w = win();
    return w?.location?.hostname ?? null;
  }

  /**
   * Пересчитать решение и подключить площадки, которым оно теперь разрешает.
   *
   * CORE-1659 — категорий две, поэтому решение может меняться частями: человек
   * разрешил аналитические, позже — маркетинговые. Каждая площадка подключается
   * один раз, когда её категория разрешена впервые; цели и просмотры идут только
   * тем, кто разрешён сейчас.
   *
   * Отключение обратно не выгружает уже загруженные скрипты — выгрузить чужой
   * тег из живой страницы нельзя, и делать вид, что можно, хуже, чем сказать
   * прямо: снятое согласие останавливает ОТПРАВКУ (подписка снимается), а
   * страницу с уже подключённым счётчиком нужно перезагрузить. Ровно поэтому
   * гейт стоит ДО первой загрузки, а не после.
   */
  private evaluate(): void {
    const decision = shouldLoadTags({
      consent: this.consent,
      region: this.region,
      providers: this.providers,
      host: this.host(),
      environments: this.environments,
    });
    this.metrikaStatus.note(decision.reason, this.consent, this.region);
    this.armMetrikaStatus();

    if (!decision.load) {
      this.detach();
      return;
    }

    const w = win();
    if (!w) return;

    const wasIdle = this.active.length === 0;
    const fresh = decision.providers.filter((p) => !this.loaded.has(p.kind));
    for (const provider of fresh) {
      try {
        if (provider.kind === 'metrika') this.loadMetrikaTracked(w, provider.id);
        else if (provider.kind === 'ga4') loadGa4(w, provider.id);
        else if (provider.kind === 'vk') loadVk(w, provider.id);
        this.loaded.add(provider.kind);
      } catch {
        // Блокировщик, отсутствующая сеть, сломанный id. Маркетинговый слой —
        // наблюдение поверх продукта: его отказ не имеет права быть заметен
        // человеку, который просто открыл страницу.
        continue;
      }
    }
    this.active = decision.providers;
    this.attach();
    const landing = metrikaLandingHit(w.location?.origin || '', webEntrySnapshot());
    for (const provider of fresh) {
      if (provider.kind !== 'metrika') continue;
      try {
        w.ym?.(provider.id, 'hit', landing.url, landing.referer ? { referer: landing.referer } : {});
      } catch {
        continue;
      }
      // Посадочную Метрика уже получила с метками — первый просмотр того же пути
      // (до или после согласия, порядок не гарантирован) второй раз ей не шлётся.
      this.metrikaLandingPath = landing.path;
    }
    // Остальное — один раз, когда слой включается: разрешённая позже вторая
    // категория не должна повторять просмотр и цель тем, кто их уже получил.
    if (!wasIdle) return;
    if (this.lastPath) this.pageView(this.lastPath);
    // CORE-1277 — «Посетил сайт». Отправляется отсюда, а не из экрана: страница
    // уже открыта (иначе этот код не выполнялся бы), и любое место в UI, откуда
    // такую цель звали бы, оказалось бы одним конкретным экраном — то есть не
    // «любой страницей», как просит инструкция.
    void this.sendGoal(siteVisitGoal());
  }

  /** CORE-1853 — один раз на страницу: через паузу сказать, если Метрика так и не грузилась. */
  private armMetrikaStatus(): void {
    if (this.metrikaStatusArmed || !win()) return;
    this.metrikaStatusArmed = true;
    setTimeout(() => this.reportMetrikaStatus(this.metrikaStatus.settle()), METRIKA_STATUS_SETTLE_MS);
  }

  private loadMetrikaTracked(w: Win, id: string): void {
    this.metrikaStatus.scriptStarted(Date.now());
    const finish = (outcome: 'loaded' | 'script_error' | 'script_timeout') =>
      this.reportMetrikaStatus(this.metrikaStatus.scriptFinished(outcome, Date.now()));
    setTimeout(() => finish('script_timeout'), METRIKA_SCRIPT_TIMEOUT_MS);
    try {
      loadMetrika(w, id, (ok) => finish(ok ? 'loaded' : 'script_error'));
    } catch (error) {
      finish('script_error');
      throw error;
    }
  }

  private reportMetrikaStatus(props: MetrikaStatusProps | null): void {
    if (!props) return;
    try {
      analytics.track(METRIKA_STATUS_EVENT, { ...props });
    } catch {
      // Наблюдение за наблюдением не имеет права ломать страницу.
    }
  }

  private attach(): void {
    if (this.unsubscribe) return;
    this.unsubscribe = analytics.onEvent((eventName, properties) => {
      void this.handleEvent(eventName, properties);
    });
  }

  private detach(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.active = [];
  }

  private async readLedger(): Promise<DedupLedger | null> {
    if (this.ledgerRead) return this.ledger;
    this.ledgerRead = true;
    this.ledger = await storage.getJSON<DedupLedger>(MARKETING_DEDUP_KEY).catch(() => null);
    return this.ledger;
  }

  private async handleEvent(
    eventName: string,
    properties: Record<string, unknown> | undefined,
  ): Promise<void> {
    if (!this.active.length) return;
    // Целей может быть несколько: успешная регистрация из CTA — это и общая
    // конверсия, и цель конкретной воронки (CORE-1277).
    for (const hit of goalsForEvent(eventName, properties)) {
      await this.sendGoal(hit);
    }
  }

  /** Отправить цель, если она в своей области видимости ещё не уходила. */
  private async sendGoal(hit: GoalHit): Promise<void> {
    if (!this.active.length) return;

    const sessionId = await analytics.getSessionId().catch(() => '');
    const previous = await this.readLedger();
    const { ledger, send } = applyGoal(previous, sessionId, hit);
    this.ledger = ledger;
    if (!send) return;

    try {
      await storage.setJSON(MARKETING_DEDUP_KEY, ledger);
    } catch {
      // Приватный режим: журнал не переживёт перезагрузку. Цель всё равно
      // отправляем — потерять конверсию хуже, чем отправить её дважды.
    }

    const state = await readState().catch(() => null);
    // Разметка цели ложится ПОВЕРХ контекста кампании, но пересечься они не
    // могут: у правила свой ключ (`goal_source`), см. `GoalRule.goalSource`.
    this.reachGoal(hit.goal, sanitizeGoalParams({ ...goalParams(state), ...(hit.params || {}) }));
  }

  private reachGoal(goal: MarketingGoal, params: Record<string, string>): void {
    const w = win();
    if (!w) return;
    for (const provider of this.active) {
      try {
        if (provider.kind === 'metrika') w.ym?.(provider.id, 'reachGoal', goal, params);
        else if (provider.kind === 'ga4') w.gtag?.('event', goal, params);
        else if (provider.kind === 'vk') w._tmr?.push({ id: provider.id, type: 'reachGoal', goal, params });
      } catch {
        continue;
      }
    }
  }

  /**
   * Переход по маршруту внутри приложения.
   *
   * Нужен потому, что это SPA: счётчики считают просмотр при загрузке страницы,
   * а её здесь ровно одна. Без этого вся навигация после входа выглядела бы в
   * Метрике как один бесконечный просмотр посадочной.
   */
  pageView(path: string): void {
    if (!path) return;
    this.lastPath = path;
    if (!this.active.length) return;
    const w = win();
    if (!w) return;
    const url = (w.location?.origin || '') + path;
    const landingPath = this.metrikaLandingPath;
    this.metrikaLandingPath = null;
    for (const provider of this.active) {
      if (provider.kind === 'metrika' && path === landingPath) continue;
      try {
        if (provider.kind === 'metrika') w.ym?.(provider.id, 'hit', url);
        else if (provider.kind === 'ga4') w.gtag?.('event', 'page_view', { page_path: path });
        else if (provider.kind === 'vk') w._tmr?.push({ id: provider.id, type: 'pageView', url });
      } catch {
        continue;
      }
    }
  }
}

export const marketingTags = new MarketingTagRuntime();
